/**
 * Money as a printed document shows it, and the currency a document is in.
 *
 * `formatMoney` in `format.ts` drops `.00` from a whole amount, which a screen
 * may do and an invoice may not: `formatDocMoney` always prints the
 * currency's minor digits (proposal.md 3.5). Amounts arrive in minor units, as
 * `document-math.ts` works them out, and are formatted from an exact decimal
 * string, never through a float.
 *
 * The currency (decision 19): `app.record.currency` is nullable, so a document
 * falls back to the tenant's `default_currency`, as the entry proposal does.
 * Neither set means the document cannot say what its money is in, and it is
 * refused rather than guessed. A GST kind requires INR.
 */

/** The currency a document prints in: the record's, else the tenant's, else null. */
export const docCurrency = (recordCurrency: string | null | undefined, tenantCurrency: string | null | undefined): string | null => {
  const tidy = (c: string | null | undefined): string | null => {
    const code = (c ?? '').trim().toUpperCase();
    return /^[A-Z]{3}$/.test(code) ? code : null;
  };
  return tidy(recordCurrency) ?? tidy(tenantCurrency);
};

const DIGITS = new Map<string, number>();

/** The currency's minor digits (2 for INR, 0 for JPY, 3 for KWD), from `Intl`; 2 when it cannot say. */
export const currencyDigits = (currency: string): number => {
  const known = DIGITS.get(currency);
  if (known !== undefined) return known;
  let digits = 2;
  try {
    digits = new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    digits = 2;
  }
  DIGITS.set(currency, digits);
  return digits;
};

/** The locale a currency's amounts group in: lakh and crore for INR. */
export const docLocaleFor = (currency: string | null): string => (currency === 'INR' ? 'en-IN' : 'en-US');

/** Minor units as an exact decimal string: 2412446 with 2 digits gives "24124.46". */
export const minorToDecimal = (minor: number | bigint, digits: number): string => {
  const value = BigInt(minor);
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const text = abs.toString().padStart(digits + 1, '0');
  const whole = digits === 0 ? text : text.slice(0, -digits);
  const frac = digits === 0 ? '' : `.${text.slice(-digits)}`;
  return `${negative ? '-' : ''}${whole}${frac}`;
};

export type DocMoneyOptions = {
  /** Print the currency's symbol (the default), or the bare amount for a table cell. */
  readonly symbol?: boolean;
};

/**
 * An amount in minor units, as a document prints it: always with the minor
 * digits ("₹24,124.00", "7,920.00"), grouped by `locale`.
 */
export const formatDocMoney = (minor: number | bigint, currency: string, locale: string = docLocaleFor(currency), options: DocMoneyOptions = {}): string => {
  const digits = currencyDigits(currency);
  const decimal = minorToDecimal(minor, digits);
  const format = new Intl.NumberFormat(locale, {
    ...(options.symbol === false ? { style: 'decimal' } : { style: 'currency', currency }),
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  // `format` takes an exact decimal string (Intl.NumberFormat v3), so no float rounds it.
  return format.format(decimal as unknown as number);
};
